import React, { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import NcImage from "shared/NcImage/NcImage";
import Axios from '_services/caller.service';
import { useTranslation } from 'react-i18next';
import { Tooltip }  from "react-tooltip";
import { getTransportIconClass } from "utils/transportIcon";

export interface CardNuitResumeProps {
  onLieuxResumeClick: () => void;
  className?: string;
  libelle: string;
  jour: string;
  nbNuits: number;
  image: string;
  images?: string[];
  imageItems?: ResumeImageItem[];
  pays: string;
  isModif: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  typeTransport?: string | null;
}

interface ResumeImageItem {
  url: string;
  caption: string;
}

const CardNuitResume: FC<CardNuitResumeProps> = ({
  onLieuxResumeClick,
  className = "",
  libelle,
  jour,
  nbNuits,
  image,
  images,
  imageItems,
  pays,
  isModif,
  isFirst,
  isLast,
  typeTransport
}) => {
 const { t } = useTranslation();
 const isTransportNight =
   libelle === "Nuit en transport" || libelle === "Night in transport";
 const transportIconClass = isTransportNight
   ? getTransportIconClass(typeTransport)
   : null;
 const isIncompleteLabel =
   libelle === t("cliquezCompleter") ||
   libelle === "Cliquez pour compléter" ||
   libelle === "Click to add";

 const resumeImageItems: ResumeImageItem[] =
   imageItems && imageItems.length > 0
     ? imageItems
     : images && images.length > 0
       ? images.map((imageUrl) => ({
         url: imageUrl,
         caption: "",
       }))
       : image
         ? [{ url: image, caption: "" }]
         : [];
 const resumeImages = resumeImageItems.map((imageItem) => imageItem.url);
 const firstImage = resumeImages[0] || "";
 const [imageLoad, setImageLoad] = useState<string | null>(null);


  // Déclarez un état pour suivre l'état d'affichage de la modale
   const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);
   const [modalImageIndex, setModalImageIndex] = useState<number | null>(null);
 
   // Fonction pour fermer la modale
   const closeModal = () => {
     setModalImageUrl(null);
     setModalImageIndex(null);
   };

 const handleLieuxResumeClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
   if(!modalImageUrl && libelle !== 'Nuit en transport' && libelle !== 'Night in transport')
   {
    onLieuxResumeClick();
   }
};


 const fetchImage = async (url: string): Promise<HTMLImageElement> => {
  try {
    const response = await Axios.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'image/jpeg' });
    const imageURL = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve(img);
        // Nous révoquerons l'URL blob après l'ajout de l'image au PDF
      };
      img.onerror = (event) => {
        console.error('Erreur lors du chargement de l\'image', event);
        reject(event);
      };
      img.src = imageURL;
    });
  } catch (error) {
    console.error('Erreur lors de la requête pour récupérer l\'image', error);
    throw error;
  }
};

useEffect(() => {

  if(firstImage)
  { 
    const fetchImageAsync = async () => {
      if (firstImage) {
        const fetchedImage = await fetchImage(firstImage);
        setImageLoad(fetchedImage.src);
      } else {
        setImageLoad(null);
      }
    };

    fetchImageAsync();
  }
  else
  {
    setImageLoad(null);
  }
}, [firstImage]); 

const handleImageClick = () => {
  if (!imageLoad) return;

  setModalImageIndex(0);
  setModalImageUrl(imageLoad);
};

const showModalImage = async (index: number) => {
  const fetchedImage = await fetchImage(resumeImages[index]);
  setModalImageIndex(index);
  setModalImageUrl(fetchedImage.src);
};

const showPreviousImage = async (event: React.MouseEvent<HTMLButtonElement>) => {
  event.preventDefault();
  event.stopPropagation();

  if (resumeImages.length === 0) return;

  const nextIndex =
    modalImageIndex === null || modalImageIndex === 0
      ? resumeImages.length - 1
      : modalImageIndex - 1;

  await showModalImage(nextIndex);
};

const showNextImage = async (event: React.MouseEvent<HTMLButtonElement>) => {
  event.preventDefault();
  event.stopPropagation();

  if (resumeImages.length === 0) return;

  const nextIndex =
    modalImageIndex === null || modalImageIndex === resumeImages.length - 1
      ? 0
      : modalImageIndex + 1;

  await showModalImage(nextIndex);
};

const modalContent = modalImageUrl
  ? (
      <div
        className="
          fixed
          inset-0
          z-[2147483647]
          flex
          items-center
          justify-center
          bg-black/85
          px-3
          sm:px-16
          py-10
        "
        onClick={closeModal}
      >
        <div
          className="
            relative
            flex
            flex-col
            max-h-[90vh]
            max-w-[90vw]
            items-center
            justify-center
            gap-4
          "
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <img
            src={modalImageUrl}
            alt=""
            className="
              max-h-[90vh]
              max-w-[90vw]
              rounded-xl
              object-contain
              shadow-2xl
            "
          />

          {modalImageIndex !== null &&
            resumeImageItems[modalImageIndex]?.caption && (
              <div
                className="
                  max-w-[90vw]
                  rounded-full
                  bg-black
                  px-5
                  py-2
                  text-center
                  text-sm
                  font-medium
                  text-white
                  ring-1
                  ring-white/20
                  shadow-lg
                "
              >
                {resumeImageItems[modalImageIndex].caption}
              </div>
            )}

          <button
            type="button"
            className="
              absolute
              -right-4
              -top-4
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-full
              bg-black/70
              text-2xl
              text-white
              hover:bg-black/90
            "
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              closeModal();
            }}
          >
            ×
          </button>

          {resumeImages.length > 1 && (
            <>
              <button
                type="button"
                className="
                  absolute
                  left-3
                  sm:-left-14
                  top-1/2
                  flex
                  h-12
                  w-12
                  -translate-y-1/2
                  items-center
                  justify-center
                  rounded-full
                  bg-black/60
                  text-5xl
                  leading-none
                  text-white
                  drop-shadow-lg
                  hover:bg-black/80
                "
                onClick={showPreviousImage}
              >
                ‹
              </button>

              <button
                type="button"
                className="
                  absolute
                  right-3
                  sm:-right-14
                  top-1/2
                  flex
                  h-12
                  w-12
                  -translate-y-1/2
                  items-center
                  justify-center
                  rounded-full
                  bg-black/60
                  text-5xl
                  leading-none
                  text-white
                  drop-shadow-lg
                  hover:bg-black/80
                "
                onClick={showNextImage}
              >
                ›
              </button>
            </>
          )}
        </div>
      </div>
    )
  : null;

useEffect(() => {
  if (!modalImageUrl) return;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      closeModal();
      return;
    }

    if (resumeImages.length <= 1) return;

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      const nextIndex =
        modalImageIndex === null || modalImageIndex === 0
          ? resumeImages.length - 1
          : modalImageIndex - 1;

      showModalImage(nextIndex);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      const nextIndex =
        modalImageIndex === null || modalImageIndex === resumeImages.length - 1
          ? 0
          : modalImageIndex + 1;

      showModalImage(nextIndex);
    }
  };

  window.addEventListener("keydown", handleKeyDown);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
  };
}, [modalImageUrl, modalImageIndex, resumeImages]);

  return (

    
  <div
    className={`
  relative
  flex
  items-stretch
  gap-0
  mb-2
  ${className}
`}
    data-nc-id="CardNuitResume"
  >

    {/* TIMELINE */}
<div
  className="
    relative
    flex
    flex-col
    items-center
    justify-center
    mr-4
  "
>

 {/* ligne haute */}
{!isFirst && (
  <div
    className="
      absolute
      -top-4
      h-[calc(50%+16px)]
      w-[2px]
      bg-[#DDD6FE]

      
    "
  />
)}

{/* ligne basse */}
{!isLast && (
  <div
    className="
      absolute
      -bottom-4
      h-[calc(50%+16px)]
      w-[2px]
      bg-[#DDD6FE]
    "
  />
)}

  {/* bulle */}
  <div
    className={`
      relative
      z-1

      w-[30px]
      h-[30px]
      text-[11px]

      px-2

      rounded-full

      bg-[#DDD6FE]
text-violet-700
shadow-[0_6px_16px_rgba(124,58,237,0.16)]

      flex
      items-center
      justify-center

      text-xs
      font-bold

      pointeurRose
    `}
    onClick={handleLieuxResumeClick}
    data-tooltip-id="myTooltip"
    data-tooltip-content={`${nbNuits} ${t("nuit")}${nbNuits > 1 ? "s" : ""}`}
    data-tooltip-place="top"
  >
    {nbNuits}N
  </div>

    <Tooltip id="myTooltip" style={{ zIndex: 9999 }} />
</div>

<div
  className={`
    group
    relative
    flex
    flex-col
    flex-1
    w-full
    justify-between
    overflow-hidden

    rounded-[24px]

    bg-[linear-gradient(145deg,rgba(255,255,255,0.98)_0%,rgba(255,255,255,0.95)_58%,rgba(250,250,252,0.96)_100%)]
    dark:bg-[linear-gradient(145deg,rgba(15,23,42,0.98)_0%,rgba(17,24,39,0.96)_55%,rgba(10,15,28,0.98)_100%)]

    border
    border-white/80
dark:border-white/10

    px-4
    py-3

    ${firstImage && imageLoad ? "min-h-[64px]" :  "min-h-[50px]" }

 shadow-[0_10px_24px_rgba(15,23,42,0.06),0_2px_8px_rgba(15,23,42,0.04)]
hover:shadow-[0_14px_28px_rgba(15,23,42,0.08),0_4px_12px_rgba(15,23,42,0.05)]

dark:shadow-[0_10px_26px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.05)]
dark:hover:shadow-[0_14px_32px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.06)]

    transition-all
    duration-300
    ease-out

    hover:-translate-y-[2px]

        ${
          (libelle !== "Nuit en transport" &&
            libelle !== "Night in transport") ||
          !isModif
            ? "pointeurRose"
            : ""
        }

        
      `}
      onClick={handleLieuxResumeClick}
    >

      {/* BADGE NUITS 
      <div
        className="
          absolute
          top-4
          right-4

          px-3
          py-1

          rounded-full

          bg-violet-50
          dark:bg-violet-900/30

          text-violet-600
          dark:text-violet-300

          text-xxs
          font-semibold

          whitespace-nowrap
        "
      >
        {nbNuits} {t("nuit")}
        {nbNuits > 1 ? "s" : ""}
      </div>*/}

{/* halo très léger */}
<div
  className="
    pointer-events-none
    absolute
    inset-0
    rounded-[24px]
    bg-[radial-gradient(circle_at_100%_100%,rgba(196,181,253,0.05)_0%,transparent_20%),radial-gradient(circle_at_0%_0%,rgba(255,255,255,0.16)_0%,transparent_18%)]
    dark:bg-[radial-gradient(circle_at_20%_0%,rgba(255,255,255,0.05)_0%,transparent_28%),radial-gradient(circle_at_100%_100%,rgba(255,255,255,0.03)_0%,transparent_24%)]
  "
/>

{/* fine bordure intérieure glossy */}
<div
  className="
    pointer-events-none
    absolute
    inset-[1px]
    rounded-[23px]
    border
    border-white/70
    dark:border-white/10
  "
/>

{/* points décoratifs triangle dans le coin bas droite */}
<div
  className="
    pointer-events-none
    absolute
    right-3
bottom-1
    z-0

    h-[22px]
    w-[22px]

    opacity-60
    dark:opacity-35
  "
>
  {[
    // colonne droite : 4 points
    { x: 18, y: 0 },
    { x: 18, y: 6 },
    { x: 18, y: 12 },
    { x: 18, y: 18 },

    // colonne milieu : 3 points
    { x: 12, y: 6 },
    { x: 12, y: 12 },
    { x: 12, y: 18 },

    // colonne gauche : 2 points
    { x: 6, y: 12 },
    { x: 6, y: 18 },

    // pointe intérieure
    { x: 0, y: 18 },
  ].map((dot, index) => (
    <span
      key={index}
      className="
        absolute
        h-[2.5px]
        w-[2.5px]
        rounded-full
        bg-violet-300
      "
      style={{
        left: `${dot.x}px`,
        top: `${dot.y}px`,
      }}
    />
  ))}
</div>

{/* CONTENU */}
<div
  className={`
    flex
    flex-col
    min-w-0
    w-full
    items-start
    text-left

    pl-1
    ${firstImage && imageLoad ? "pr-16" : ""}
  `}
>

  {/* DATE */}
  <span
    className="
      text-xxs
      font-medium
      text-violet-500
      dark:text-[#FF4A52]
      no-slider-margin
      mb-3
    "
  >
    {jour}
  </span>

  {/* VILLE */}
  <h2
    className={`
      text-xs
      ${isIncompleteLabel ? "font-normal" : "font-semibold"}
      text-neutral-900
      dark:text-white

      leading-normal
      break-words
      w-full
      max-w-full
    `}
  >
    <span
      className={
        pays === t("nuitsIncompletes")
          ? "couleurRose no-slider-margin"
          : "no-slider-margin"
      }
    >
       {transportIconClass && (
         <i
           className={`${transportIconClass} mr-1`}
           aria-hidden="true"
         />
       )}
       {libelle}
    </span>
  </h2>

</div>

      {/* ZONE IMAGE FIXE */}
<div
  className="
    absolute
    top-1/2
    -translate-y-1/2
    right-2

    w-12
    h-12

    flex
    items-center
    justify-center
  "
>
  {firstImage && imageLoad && (
    <div
      className="
        w-12
        h-12

        rounded-2xl
        overflow-hidden

        shadow-md
        cursor-pointer

        hover:scale-105
        transition-transform
      "
      onClick={(e) => {
        e.stopPropagation();
        handleImageClick();
      }}
    >
      <NcImage
        src={imageLoad}
        containerClassName="
          w-full
          h-full
        "
      />
    </div>
  )}
</div>

      {modalContent && createPortal(modalContent, document.body)}

    </div>

  </div>
);
};

export default CardNuitResume;
